<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import SerpFilterGroup from './SerpFilterGroup.vue';
  import { SERP_FILTERS_TEXTS } from '../config/texts';
  import { RATING_FILTER_THRESHOLDS } from '../lib/filterProducts';
  import type { SerpFilterGroupId, SerpFilterOptionView, SerpOptionsFilterGroupView } from '../model/types';
  import { useSerpFilters } from '../model/useSerpFilters';

  interface RatingFilterProps {
    group: SerpOptionsFilterGroupView;
  }

  defineProps<RatingFilterProps>();

  const RATING_FRACTION_DIGITS = 1;
  const EXCELLENT_MIN_RATING = 4;

  type RatingBadgeTone = 'excellent' | 'good';

  interface RatingBadge {
    label: string;
    tone: RatingBadgeTone;
  }

  const ANY_RATING_BADGE: RatingBadge = { label: SERP_FILTERS_TEXTS.ratingAnyBadge, tone: 'excellent' };

  const RATING_BADGES: Readonly<Record<string, RatingBadge>> = Object.fromEntries(
    Object.entries(RATING_FILTER_THRESHOLDS).map(([key, minRating]): [string, RatingBadge] => [
      key,
      {
        label: minRating.toFixed(RATING_FRACTION_DIGITS),
        tone: minRating >= EXCELLENT_MIN_RATING ? 'excellent' : 'good',
      },
    ]),
  );

  const { toggle, clearGroup } = useSerpFilters();

  const badgeOf = (option: SerpFilterOptionView): RatingBadge => RATING_BADGES[option.key] ?? ANY_RATING_BADGE;

  const onSelect = (groupId: SerpFilterGroupId, key: string): void => {
    toggle(groupId, key);
  };

  const onReset = (groupId: SerpFilterGroupId): void => {
    clearGroup(groupId);
  };
</script>

<template>
  <SerpFilterGroup
    class="rating-filter"
    :group="group"
    @select="onSelect"
    @reset="onReset"
  >
    <template #option-prepend="{ option }">
      <Typo
        :class="['rating-filter__badge', `rating-filter__badge_${badgeOf(option).tone}`]"
        :variant="TypoVariant.BodyMMedium"
        tag="span"
        :data-testid="`rating-filter-badge-${option.key || 'any'}`"
      >
        {{ badgeOf(option).label }}
      </Typo>
    </template>
  </SerpFilterGroup>
</template>

<style scoped lang="scss">
  .rating-filter {
    &__badge {
      border-radius: var(--rounding-xxl);
      box-sizing: border-box;
      color: var(--color-neutral-0);
      display: inline-flex;
      flex-shrink: 0;
      justify-content: center;
      min-width: 40px;
      padding: var(--spacing-xxs);

      &_excellent {
        background: var(--color-green-50);
      }

      &_good {
        background: var(--color-lime-50);
      }
    }
  }
</style>
